import { CheckCircle2, Loader2 } from 'lucide-react';
import type { TrainingVideo } from '../../../types';
import { getEmbedUrl, resolveVideoUrl } from '../lib';

interface VideoPlayerProps {
    video: TrainingVideo;
    onMarkWatched: () => void;
    isMarking: boolean;
}

export default function VideoPlayer({ video, onMarkWatched, isMarking }: VideoPlayerProps) {
    const src = resolveVideoUrl(video.videoUrl);
    const embedUrl = src ? getEmbedUrl(src) : null;

    return (
        <div>
            <div className="aspect-video w-full overflow-hidden rounded-2xl bg-black">
                {!src ? (
                    <div className="flex h-full items-center justify-center text-sm text-gray-400">
                        Video unavailable
                    </div>
                ) : embedUrl ? (
                    <iframe
                        src={embedUrl}
                        title={video.title}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                ) : (
                    <video
                        src={src}
                        controls
                        className="h-full w-full"
                        onEnded={() => {
                            if (!video.watched) onMarkWatched();
                        }}
                    />
                )}
            </div>

            <div className="mt-4 flex items-center justify-between gap-4">
                <p className="text-xs text-gray-500">
                    {embedUrl
                        ? 'Watch the full video, then mark it complete to unlock the quiz.'
                        : 'The quiz unlocks automatically when the video finishes.'}
                </p>
                {video.watched ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700">
                        <CheckCircle2 className="h-4 w-4" /> Watched
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={onMarkWatched}
                        disabled={isMarking}
                        className="inline-flex shrink-0 items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50"
                    >
                        {isMarking && <Loader2 className="h-4 w-4 animate-spin" />}
                        I&apos;ve watched this
                    </button>
                )}
            </div>
        </div>
    );
}
